import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SwalService } from 'src/app/services/alert/swal.service';
import { EventClosingComponent } from '../event-closing/event-closing.component';
import { EventClosingService, BoxTotals } from 'src/app/services/event-closing/event-closing.service';

/**
 * Auditoría de un evento (rol 11): todas las cajas que mandaron órdenes,
 * cuánto reportó cada una y si ya cerró. El rol 11 no arquea: revisa los
 * cierres de las cajas y firma el evento.
 */
@Component({
    selector: 'app-event-audit',
    templateUrl: './event-audit.component.html',
    styleUrls: ['./event-audit.component.scss'],
})
export class EventAuditComponent implements OnInit {

    @Input() event_key: string = '';
    @Input() event_name: string = '';
    /**
     * Órdenes del evento. Si la página no las pasa —o pasa solo las de una
     * zona, como monitor-v3— el modal las trae completas por su cuenta.
     */
    @Input() orders: any[] = [];

    stores: BoxTotals[] = [];
    closingRequired: boolean = false;
    validation: any = null;

    loading: boolean = true;
    saving: boolean = false;
    notes: string = '';

    constructor(
        private modalCtrl: ModalController,
        private eventClosing: EventClosingService,
        private SwalService: SwalService,
        public auth: AuthService,
    ) { }

    async ngOnInit() {
        await this.load();
    }

    async load() {
        this.loading = true;
        try {
            this.closingRequired = await this.eventClosing.isClosingRequired(this.event_key);

            // Siempre el evento completo: la auditoría es de todas las cajas.
            const allOrders = await this.eventClosing.getEventOrders(this.event_key);
            const source = allOrders.length > 0 ? allOrders : (this.orders || []);

            let stores = this.eventClosing.groupOrdersByBox(source);
            stores = await this.eventClosing.resolveBoxNames(stores);

            // Los cierres se cruzan siempre: aunque el evento no exija cierre,
            // el responsable puede haber cerrado las cajas desde aquí.
            const closings = await this.eventClosing.getClosingsForEvent(this.event_key);
            stores = this.eventClosing.attachClosings(stores, closings);

            // Cada caja con sus cajeros: la caja suma a todos sus usuarios.
            stores.forEach(store => store.cashiers = this.eventClosing.groupOrdersByCashier(source, store.box_key));
            this.stores = stores;
            const allCashiers = [].concat(...stores.map(store => store.cashiers || []));
            this.eventClosing.resolveCashierNames(allCashiers).catch(() => { });

            this.validation = await this.eventClosing.getValidation(this.event_key);
            if (this.validation && !this.notes) this.notes = this.validation.notes || '';
        } catch (e) {
            console.error('Error cargando la auditoría:', e);
        }
        this.loading = false;
    }

    // ── Consolidado ────────────────────────────────────────────────────────

    get total_sales(): number { return this.sum('total_sales'); }
    get total_cash(): number { return this.sum('total_cash'); }
    get total_card(): number { return this.sum('total_card'); }
    get total_pos(): number { return this.sum('total_pos'); }
    get total_orders(): number { return this.sum('total_orders'); }

    private sum(field: string): number {
        const total = (this.stores || []).reduce((acc, s) => acc + Number(s[field] || 0), 0);
        return Math.round(total * 100) / 100;
    }

    /** Solo cuentan las cajas cerradas en firme, no las que solo pre-cerraron. */
    get closed_count(): number {
        return (this.stores || []).filter(s => this.eventClosing.isFinal(s.closing)).length;
    }

    get pre_only_count(): number {
        return (this.stores || []).filter(s => this.eventClosing.isPreOnly(s.closing)).length;
    }

    storeIsFinal(store: BoxTotals): boolean {
        return this.eventClosing.isFinal(store && store.closing);
    }

    storeIsPreOnly(store: BoxTotals): boolean {
        return this.eventClosing.isPreOnly(store && store.closing);
    }

    get pending_count(): number {
        return (this.stores || []).length - this.closed_count;
    }

    /** Suma de descuadres de las cajas que sí arquearon. */
    get total_difference(): number {
        const total = (this.stores || []).reduce((acc, s) => {
            const d = s.closing && s.closing.difference;
            return acc + (typeof d === 'number' ? d : 0);
        }, 0);
        return Math.round(total * 100) / 100;
    }

    get has_differences(): boolean {
        return (this.stores || []).some(s => this.storeIsOff(s));
    }

    /** true si esa caja cerró con un descuadre mayor a un centavo. */
    storeIsOff(store: BoxTotals): boolean {
        const d = store && store.closing ? store.closing.difference : null;
        return typeof d === 'number' && Math.abs(d) >= 0.01;
    }

    storeState(store: BoxTotals): string {
        if (!store.closing) return this.closingRequired ? 'Sin cerrar' : '';
        if (this.storeIsPreOnly(store)) {
            return this.storeIsOff(store) ? 'Pre-cierre, pendiente de revisión' : 'Pre-cierre, sin cerrar';
        }
        const d = store.closing.difference;
        if (d === null || d === undefined) return 'Cerró sin arqueo';
        return this.storeIsOff(store) ? 'Cerró, pendiente de revisión' : 'Cerró y coincide';
    }

    /** Responsables y administradores (0, 1, 2, 11, 18) cierran cualquier caja, con todos sus cajeros. */
    get can_manage(): boolean {
        return this.eventClosing.canToggleEventClosing();
    }

    /** Cajas con órdenes que aún no cerraron en firme (las sin zona no se cierran). */
    get open_stores(): BoxTotals[] {
        return (this.stores || []).filter(s => s.box_key !== 'sin_caja' && !this.eventClosing.isFinal(s.closing));
    }

    storeButtonLabel(store: BoxTotals): string {
        if (!this.can_manage) return this.storeIsFinal(store) ? 'Ver el cierre de esta caja' : 'Ver el pre-cierre';
        if (this.storeIsFinal(store)) return 'Ver o rehacer el cierre';
        if (this.storeIsPreOnly(store)) return 'Continuar el cierre';
        return 'Cerrar esta caja';
    }

    /** Cierra en firme todas las cajas abiertas con los totales del sistema (todos los cajeros), sin arqueo físico. */
    async closeAllStores() {
        if (this.saving || !this.can_manage) return;
        const open = this.open_stores;
        if (open.length === 0) {
            this.SwalService.fire('Nada que cerrar', 'Todas las cajas ya están cerradas en firme.', 'info');
            return;
        }
        const total = Math.round(open.reduce((acc, s) => acc + Number(s.total_sales || 0), 0) * 100) / 100;
        const result: any = await this.SwalService.fire({
            title: `¿Cerrar ${open.length} ${open.length === 1 ? 'caja' : 'cajas'}?`,
            text: `${open.map(s => s.box_name || s.box_key).join(', ')}: se cierran con los totales del sistema, sumando a todos los cajeros de cada caja (${this.auth.currency}${total.toFixed(2)}), sin arqueo físico. Un cierre ya hecho por una caja se reemplaza.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#912721',
            cancelButtonColor: '#92949c',
            confirmButtonText: 'Sí, cerrar todas',
            cancelButtonText: 'Cancelar',
        });
        if (!result || !result.value) return;

        this.saving = true;
        try {
            const count = await this.eventClosing.closeAllBoxes(this.event_key, this.event_name, open);
            this.SwalService.fire('¡Listo!', `${count} ${count === 1 ? 'caja cerrada' : 'cajas cerradas'} con los totales de todos sus cajeros.`, 'success');
            await this.load();
        } catch (e) {
            console.error('No se pudieron cerrar las cajas:', e);
            this.SwalService.fire('Error', 'No se pudieron cerrar todas las cajas. Intenta de nuevo.', 'error');
        }
        this.saving = false;
    }

    // ── Acciones ───────────────────────────────────────────────────────────

    /**
     * Abre el cierre de una caja. Quien administra el evento lo hace en
     * modo edición y con TODAS las órdenes de la caja (todos sus cajeros:
     * account_key vacío); los demás solo consultan.
     */
    async viewStoreClosing(store: BoxTotals) {
        const modal = await this.modalCtrl.create({
            component: EventClosingComponent,
            cssClass: 'eventClosingModal',
            componentProps: {
                event_key: this.event_key,
                event_name: this.event_name,
                box_key: store.box_key,
                box_name: store.box_name,
                readonly: !this.can_manage,
                account_key: '',
                total_orders: store.total_orders,
                total_sales: store.total_sales,
                total_cost_admin: 0,
                total_cash: store.total_cash,
                total_card: store.total_card,
                total_pos: store.total_pos,
            }
        });
        modal.onDidDismiss().then(() => this.load());
        return await modal.present();
    }

    async confirmAudit() {
        if (this.saving) return;

        // Firmar con cajas sin cerrar es posible, pero no por descuido.
        if (this.closingRequired && this.pending_count > 0) {
            const result: any = await this.SwalService.fire({
                title: 'Hay cajas sin cerrar',
                text: `${this.pending_count} de ${this.stores.length} cajas todavía no cierran en firme`
                    + (this.pre_only_count > 0 ? ` (${this.pre_only_count} solo hicieron pre-cierre)` : '')
                    + '. ¿Firmar la auditoría de todos modos?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, firmar',
                cancelButtonText: 'Esperar',
            });
            if (!result || !result.value) return;
        }

        this.saving = true;
        try {
            await this.eventClosing.validateReport(this.event_key, this.event_name, this.notes);
            this.SwalService.fire('¡Listo!', 'La auditoría del evento quedó registrada.', 'success');
            this.modalCtrl.dismiss({ audited: true });
        } catch (e) {
            console.error('No se pudo guardar la auditoría:', e);
            this.SwalService.fire('Error', 'No se pudo guardar la auditoría. Intenta de nuevo.', 'error');
        }
        this.saving = false;
    }

    dismiss() {
        this.modalCtrl.dismiss();
    }
}
